/**
 * PreProcess template engine that uses ONLY pre-parsed template structures for merging.
 * NO IndexOf parsing at merge time - all structural information comes from PreprocessedTemplate.
 *
 * Architecture:
 * - Loader: Load, Parse, PreProcess (creates PreprocessedTemplate with Placeholders, SlottedTemplates, etc.)
 * - Engine: Only Merging using pre-parsed structures (NO parsing)
 *
 * JSON merging always happens in the engine at merge time, never in the loader.
 */

import { TemplateOperation } from '../../model/index.js';
import { PreprocessedTemplate } from '../model/index.js';
import { MergeResult } from '../../model/mergeResult.js';
import { IEngine } from '../../base/engine/iEngine.js';
import type { PhaseTiming } from '../../base/timing/phaseTiming.js';

import { JsonObject, JsonValue } from '../../app/json/jsonValue.js';
import { CommonUtil } from '../../common/commonUtil.js';
import type { DynamicResults } from '../../model/dynamicResults.js';
import type { SlottedTemplate } from '../model/slottedTemplate.js';
import type { TemplatePlaceholder } from '../model/templatePlaceholder.js';
import type { SlotPlaceholder } from '../model/slotPlaceholder.js';

// Import from split files
import { mergeTemplates, prepareProtectedMergeContext, initializeDynamicMergeState, unescapeProtectedDataValues, collectDynamicHandlerResults } from './enginePreProcess_Merge.js';
import { processTemplateContent, resolveLatePlaceholder, processSlottedTemplate, processSlotContent, processPlaceholder } from './enginePreProcess_Merge_TemplateTraversal.js';
import type { ILoaderPreProcess } from '../loader/iLoaderPreProcess.js';

/**
 * PreProcess template engine
 */
export class EnginePreProcess extends IEngine {

    // #region Private Variable

    // Loader provided at construction time - ensures type safety
    _loader: ILoaderPreProcess | null = null;

    // #endregion

    // #region Merge Context

    createProtectedMergeContext(appSite: string, appView: string | null, appFile: string, engineType: string, contextJson: JsonObject): JsonObject {
        // Build a FRESH immutable merge context. The identity keys are written first onto the new
        // (always mutable) object, so the write can never be silently dropped by a read-only source —
        // that silent drop is the leak that let a stale/blank EngineType survive in some paths/langs.
        // The caller's keys are then copied in only where they don't collide, so the caller can never
        // override AppSite/AppView/AppFile/EngineType/AssemblerVersion. The passed contextJson is never
        // mutated (its values are cloned, nothing is stamped onto it, it is never locked), so the same
        // instance flows unchanged into every engine and serializes plainly into the api-json (no leaked
        // EngineType to diverge across engines or config sources).
        const mergeContextJson = new JsonObject();
        mergeContextJson.set('AppSite', JsonValue.string(appSite));
        mergeContextJson.set('AppView', JsonValue.string(appView || ''));
        mergeContextJson.set('AppFile', JsonValue.string(appFile));
        mergeContextJson.set('EngineType', JsonValue.string(engineType));
        mergeContextJson.set('AssemblerVersion', JsonValue.string(CommonUtil.AssemblerVersion));
        for (const [key, value] of contextJson.entries()) {
            if (!mergeContextJson.containsKey(key)) {
                mergeContextJson.set(key, value.deepClone());
            }
        }
        mergeContextJson.isReadOnly = true;
        return mergeContextJson;
    }

    createProtectedMergeConfig(configJson: JsonObject | null): JsonObject | null {
        if (configJson) {
            configJson.isReadOnly = true;
        }
        return configJson;
    }

    // #endregion

    // #region Constructor

    /**
     * Creates a new EnginePreProcess instance with the specified loader.
     * The loader is stored and used for all subsequent operations, ensuring type safety.
     */
    constructor(loader: ILoaderPreProcess) {
        super();
        if (!loader) {
            throw new Error('loader is required');
        }
        this._loader = loader;
    }

    // #endregion

    // Methods implemented via Object.assign from partial-class files
    declare _processTemplateContent: (content: string, template: PreprocessedTemplate, appSite: string, currentTemplateName: string, appView: string | null, templateKey: string, operations: TemplateOperation[], recursionDepth: number, componentHtml: JsonObject, genComponentHtml: boolean, timing: PhaseTiming, contextJson: JsonObject | null, flowStack: string[] | null, configJson: JsonObject | null, latePlaceholders?: string[] | null, clientMode?: boolean) => string;
    declare _resolveLatePlaceholder: (content: string, searchPos: number, placeholderName: string, appSite: string, currentTemplateName: string, appView: string | null, templateKey: string, operations: TemplateOperation[], recursionDepth: number, componentHtml: JsonObject, genComponentHtml: boolean, timing: PhaseTiming, contextJson: JsonObject | null, flowStack: string[] | null, configJson: JsonObject | null, clientMode?: boolean) => { result: string; searchPos: number };
    declare _processSlottedTemplate: (slottedTemplate: SlottedTemplate, appSite: string, currentTemplateName: string, appView: string | null, parentTemplateKey: string, operations: TemplateOperation[], recursionDepth: number, componentHtml: JsonObject, genComponentHtml: boolean, timing: PhaseTiming, contextJson: JsonObject | null, flowStack: string[] | null, configJson: JsonObject | null, clientMode?: boolean) => string;
    declare _processSlotContent: (slot: SlotPlaceholder, appSite: string, currentTemplateName: string, appView: string | null, contextTemplateKey: string, operations: TemplateOperation[], recursionDepth: number, componentHtml: JsonObject, genComponentHtml: boolean, timing: PhaseTiming, contextJson: JsonObject | null, flowStack: string[] | null, configJson: JsonObject | null, clientMode?: boolean) => string;
    declare _processPlaceholder: (placeholder: TemplatePlaceholder, appSite: string, currentTemplateName: string, appView: string | null, parentTemplateKey: string, operations: TemplateOperation[], recursionDepth: number, componentHtml: JsonObject, genComponentHtml: boolean, timing: PhaseTiming, contextJson: JsonObject | null, flowStack: string[] | null, configJson: JsonObject | null, clientMode?: boolean) => string;
    declare mergeTemplates: (appSite: string, appView: string | null, genOperationJson: boolean, genComponentHtml: boolean, contextJson: JsonObject | null, timing: PhaseTiming, configJson?: JsonObject | null, actionMode?: boolean, clientMode?: boolean) => MergeResult;
    declare prepareProtectedMergeContext: (appSite: string, appView: string | null, appFile: string, contextJson: JsonObject | null, configJson: JsonObject | null) => { mergeContextJson: JsonObject | null; mergeConfigJson: JsonObject | null };
    declare initializeDynamicMergeState: (appSite: string, appFile: string, appView: string | null, actionMode: boolean) => DynamicResults;
    declare unescapeProtectedDataValues: (componentHtml: JsonObject) => void;
    declare collectDynamicHandlerResults: (dynamicResults: DynamicResults, timing: PhaseTiming) => { dynamicData: JsonObject; dynamicView: JsonObject; dynamicAction: JsonObject; dynamicClientData: JsonObject; dynamicClient: JsonObject };
}

Object.assign(EnginePreProcess.prototype, {
    mergeTemplates,
    prepareProtectedMergeContext,
    initializeDynamicMergeState,
    unescapeProtectedDataValues,
    collectDynamicHandlerResults,
    _processTemplateContent: processTemplateContent,
    _resolveLatePlaceholder: resolveLatePlaceholder,
    _processSlottedTemplate: processSlottedTemplate,
    _processSlotContent: processSlotContent,
    _processPlaceholder: processPlaceholder
});

export default EnginePreProcess;
